import { defineComponent as _defineComponent } from 'vue'
export interface Props {
  assignments?: Assignment[];
  reports?: Report[];
}

/** ... */
type ActivityItem = AssignmentItem | CertificationItem | LatestReportItem;


import { ref, computed } from 'vue';
import sortBy from 'lodash/sortBy';
import uniqueId from 'lodash/uniqueId';

import { auth, RoleId } from '@auth';
import { secToHmsFilter } from '@filters/sec-to-hms';
import { modals } from '@modals';
import type { Assignment, Report, Certification } from '@models';
import { useStore, Certifications } from '@store';
import { dates } from '@utils/dates';

import CardLoadPlaceholder from '@components/Cards/CardLoadPlaceholder.vue';
import LabelCertificationStatus from '@components/LabelCertificationStatus.vue';

interface BaseItem {
  id: string;
  name: string;
  buttonText: string;
  fn: () => void;
}

interface AssignmentItem extends BaseItem {
  type: 'assignment';
  item: Assignment;
}

interface CertificationItem extends BaseItem {
  type: 'certification';
  item: Certification;
  status: Certification.Status;
}

interface LatestReportItem extends BaseItem {
  type: 'latest report';
  item: Report;
}

declare module 'vue/types/vue' {
  export interface Vue {
    CardRecentActivity: ComponentWithProps<Props>;
  }
}

const __default__ = { name: 'CardRecentActivity' };

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    assignments: null,
    reports: null
  },
  setup(__props: any) {

const props = __props as {
  assignments?: Assignment[];
  reports?: Report[];
};

/**
 * `CardRecentActivity` component properties.
 */
const store = useStore();



const loading = ref(false);

const me = computed(() => store.state.me);

// const roleId = computed(() => {
//   return store.state.me.selectedRole?.roleId;
// });

const listMyCertifications: Certifications['listMine'] = () => {
  return store.dispatch('certifications/listMine');
};

const listAvailableCertifications: Certifications['listAvailable'] = () => {
  return store.dispatch('certifications/listAvailable');
};

/** ... */
const items = computed(() => {
  const activityItems: ActivityItem[] = [];

  for (const cert of store.state.certifications.items) {
    if (cert.user.id === me.value.id) {
      // My Certifications
      activityItems.push(createOwnedCertificationItem(cert));
    } else if (
      cert.status === 'AWAITING_REVIEW' ||
      (cert.status === 'REVIEWING' && cert.reviewer?.id === me.value.id)
    ) {
      // Available Certifications for Review or Being Reviewed by Me
      // TODO (Alex) make sure once a cert is reviewed that it's removed from this list
      activityItems.push(createReviewerCertificationItem(cert));
    }
  }

  // Assignments?
  for (const a of props.assignments ?? []) {
    if (dates.isBefore(new Date(), a.dueDate)) {
      activityItems.push(createAssignmentItem(a));
    }
  }

  // Reports?

  // ...
  const latestReport = sortBy(props.reports ?? [], ['createdAt']).pop();

  if (latestReport) {
    activityItems.push(createLatestReportItem(latestReport));
  }

  return activityItems;
});

async function load() {
  loading.value = true;

  const promises: Promise<unknown>[] = [listMyCertifications()];

  if (auth.isActiveRole(RoleId.Reviewer))
    promises.push(listAvailableCertifications());

  await Promise.all(promises);

  loading.value = false;
}

void load();

//#region Helper Functions

/**
 * ...
 *
 * @param cert ...
 * @returns ...
 */
function createOwnedCertificationItem(cert: Certification) {
  const buttonText =
    cert.status === 'NOT_STARTED' ||
    cert.status === 'AWAITING_ATTEMPT' ||
    cert.status === 'AWAITING_REATTEMPT'
      ? 'Submit'
      : 'View';

  const fn = () => {
    if (
      cert.status === 'NOT_STARTED' ||
      cert.status === 'AWAITING_ATTEMPT' ||
      cert.status === 'AWAITING_REATTEMPT'
    ) {
      void modals.certifications.submitAttempt({ certificationId: cert.id });
    } else if (cert.status === 'DENIED') {
      const text = `Denied At: ${
        cert.deniedAt
          ? dates.format(cert.deniedAt, 'MM/dd/yyyy hh:mm:ss a')
          : 'N/A'
      }\n\nNotes: ${cert.notes ? cert.notes : 'Notes Not Found'}`;

      const options = { title: 'REVIEW NOTES', text };

      void modals.util.generalInfo(options);
    } else {
      void modals.certifications.view({ certificationId: cert.id });
    }
  };

  const item: CertificationItem = {
    id: cert.id,
    item: cert,
    name: cert.product.name,
    status: cert.status,
    buttonText,
    type: 'certification',
    fn,
  };

  return item;
}

/**
 * ...
 *
 * @param cert ...
 * @returns ...
 */
function createReviewerCertificationItem(cert: Certification) {
  const name = `${cert.product.name} | ${cert.user.lastName}, ${cert.user.firstName}`;

  const fn = () => {
    void modals.certifications.reviewAttempt({ certificationId: cert.id });
  };

  const item: CertificationItem = {
    id: cert.id,
    item: cert,
    name,
    status: cert.status,
    buttonText: 'Review',
    type: 'certification',
    fn,
  };

  return item;
}

/**
 * ...
 *
 * @param a ...
 * @returns ...
 */
function createAssignmentItem(a: Assignment) {
  const name = `Due: ${dates.format(a.dueDate, 'MM/DD/YYYY')} | ${
    a.scene.name
  }/${a.module.name}`;

  const fn = () => {
    const text = `Scene: ${a.scene.name}\nModule: ${
      a.module.name
    }\nDate Range: ${
      a.startDate ? dates.format(a.startDate, 'MM/DD/YYYY') : 'N/A'
    } - ${
      a.endDate ? dates.format(a.endDate, 'MM/DD/YYYY') : 'N/A'
    }\nMinimum Score: ${
      a.minimumScore ? a.minimumScore : 'N/A'
    }\nTotal Time (hh:mm:ss): ${
      a.totalTime ? secToHmsFilter(a.totalTime) : 'N/A'
    }\nDetails: ${a.details ? a.details : 'N/A'}`;

    const options = {
      title: 'ASSIGNMENT DETAILS',
      text,
    };

    // TODO (Alex) calculate if student has completed assignment

    void modals.util.generalInfo(options);
  };

  const item: AssignmentItem = {
    id: a.id,
    item: a,
    name,
    type: 'assignment',
    buttonText: 'View',
    fn,
  };

  return item;
}

/**
 * ...
 *
 * @param report ...
 * @returns ...
 */
function createLatestReportItem(report: Report) {
  const name = `${dates.format(report.createdAt, 'MM/DD/YYYY HH:mm')} | ${
    report.scene.name
  } | ${report.module.name}`;

  const fn = () => {
    void modals.report.viewReport({ report });
  };

  const item: LatestReportItem = {
    id: uniqueId(),
    item: report,
    name,
    buttonText: 'View',
    type: 'latest report',
    fn,
  };

  return item;
}

//#endregion Helper Functions

return { __sfc: true,store, props, loading, me, listMyCertifications, listAvailableCertifications, items, load, createOwnedCertificationItem, createReviewerCertificationItem, createAssignmentItem, createLatestReportItem, CardLoadPlaceholder, LabelCertificationStatus }
}

})